import { TableColumnsProp } from 'src/app/shared/models/tables.model';
import {
  AccountsPayableSummaryRow,
  IApChargeGridData,
} from './accounts-payable.model';
import {
  FormConfig,
  IBasicSearchPaginationProps,
} from 'src/app/shared/models/dynamic.model';
import { DataTableConfig } from 'src/app/shared/components/data-table';
import {
  BulkDeleteItemNames,
  GridColumnsStorageNames,
  MessageTemplates,
  gridMessages,
} from 'src/app/shared/utility/constants';

const isSelectedAndDefaultView = {
  isSelected: true,
  defaultView: true,
};

export const accountPayableExcelHeaders: string[] = [
  'Invoice Number',
  'Invoice Date',
  'Agent Name',
  'Agent Code',
  'Invoice Reference',
  'Incoming Charge Currency',
  'Conversion Rate',
  'Published to GP',
  'Notes',
  'Invoice Charges ($)',
];

export const apChargeMiscellaneousKeys: string[] = [
  'weight',
  'shipmentId',
  'destination',
  'destinationId',
  'shipmentIdentifierValue',
];

export const apChargeMiscellaneousPayloadKeys: string[] = [
  'tempId',
  'weight',
  'shipmentId',
  'destination',
  'isRowDisable',
  'destinationId',
  'shipmentIdentifierValue',
];

export const accountPayableSearchConfig: IBasicSearchPaginationProps = {
  fromIndex: 0,
  toIndex: 10,
  sortingOrder: 'desc',
  sortingField: 'mawb',
};

export const apSummaryGridColumnConfig: TableColumnsProp[] = [
  {
    label: 'Invoice Number',
    ...isSelectedAndDefaultView,
    key: 'invoiceNumber',
    defaultPrintWidth: 160,
  },
  {
    label: 'Invoice Date',
    key: 'invoiceDate',
    ...isSelectedAndDefaultView,
    defaultPrintWidth: 140,
  },
  {
    ...isSelectedAndDefaultView,
    key: 'agentName',
    label: 'Agent Name',
    defaultPrintWidth: 180,
  },
  {
    key: 'agentCode',
    label: 'Agent Code',
    ...isSelectedAndDefaultView,
    defaultPrintWidth: 120,
  },
  {
    label: 'Invoice Reference',
    ...isSelectedAndDefaultView,
    key: 'invoiceReference',
    defaultPrintWidth: 180,
  },
  {
    label: 'Incoming Charge Currency',
    key: 'incomingChargeCurrency',
    isSelected: false,
    defaultView: false,
    defaultPrintWidth: 180,
  },
  {
    label: 'Conversion Rate',
    key: 'conversionRate',
    isSelected: false,
    defaultView: false,
    defaultPrintWidth: 140,
  },
  {
    label: 'Published to GP',
    key: 'isPublish',
    ...isSelectedAndDefaultView,
    defaultPrintWidth: 140,
  },
  {
    label: 'Notes',
    key: 'notes',
    isSelected: false,
    defaultView: false,
    defaultPrintWidth: 200,
  },
  {
    key: 'invoiceCharges',
    label: 'Invoice Charges ($)',
    isSelected: false,
    defaultView: false,
    defaultPrintWidth: 120,
  },
];

export const apInvoiceInfoFormFieldConfig: FormConfig = {
  fields: [
    {
      id: 'agentValue',
      required: true,
      requiredError: 'Agent is required',
      type: 'input',
      label: 'Agent',
    },
    {
      type: 'input',
      id: 'invoiceNumber',
      requiredError: 'Invoice Number is required',
      label: 'Invoice Number',
      required: true,
      allowAlphaNumeric: true,
      maxValue: 126,
    },
    {
      type: 'datepicker',
      id: 'invoiceDate',
      required: true,
      label: 'Invoice Date',
      requiredError: 'Invoice Date is required',
    },
    {
      type: 'input',
      id: 'invoiceReference',
      isCheckForMaxValue: true,
      maxValue: 126,
      allowAlphaNumeric: true,
      label: 'Invoice Reference',
    },
    {
      id: 'incomingChargeCurrency',
      type: 'input',
      label: 'Incoming Charge Currency',
      required: true,
      requiredError: 'Incoming Charge Currency is required',
    },
    {
      id: 'conversionRate',
      type: 'input',
      label: 'Conversion Rate',
      required: true,
      requiredError: 'Conversion Rate is required',
      allowNumericWithDecimal: true,
      maxValue: 13,
    },
    {
      id: 'notes',
      label: 'Notes',
      type: 'textarea',
      maxValue: 500,
      isCheckForMaxValue: true,
      noInitialWhiteSpace: true,
    },
    {
      id: 'id',
      type: 'extra',
    },
    {
      id: 'agentId',
      type: 'extra',
    },
  ],
};

export const apChargeGridColumnConfig: TableColumnsProp[] = [
  {
    key: 'shipmentIdentifier',
    ...isSelectedAndDefaultView,
    label: 'Shipment Identifier',
    defaultPrintWidth: 100,
  },
  {
    label: 'Shipment Identifier Value',
    ...isSelectedAndDefaultView,
    key: 'shipmentIdentifierValue',
    defaultPrintWidth: 130,
  },
  {
    ...isSelectedAndDefaultView,
    label: 'Incoming Charge Amount',
    key: 'incomingChargeAmount',
    defaultPrintWidth: 130,
  },
  {
    ...isSelectedAndDefaultView,
    label: 'Charge Amount ($)',
    key: 'chargeAmount',
    defaultPrintWidth: 130,
  },
  {
    ...isSelectedAndDefaultView,
    label: 'Destination',
    key: 'destination',
    defaultPrintWidth: 130,
  },
  {
    ...isSelectedAndDefaultView,
    label: 'Weight',
    key: 'weight',
    defaultPrintWidth: 130,
  },
  {
    key: 'chargeType',
    label: 'Charge Type',
    ...isSelectedAndDefaultView,
    defaultPrintWidth: 130,
  },
  {
    label: 'Agent Charge Code',
    ...isSelectedAndDefaultView,
    key: 'agentChargeCode',
    defaultPrintWidth: 130,
  },
  {
    label: 'Agent Charge Description',
    ...isSelectedAndDefaultView,
    key: 'agentChargeDescription',
    defaultPrintWidth: 150,
  },
  {
    label: 'Shipment ID / MAWB ID',
    ...isSelectedAndDefaultView,
    key: 'shipmentId',
    defaultPrintWidth: 130,
  },
];

export const apChargeGridFormConfig: FormConfig = {
  fields: [
    {
      id: 'shipmentIdentifier',
      type: 'select',
    },
    {
      type: 'input',
      id: 'shipmentIdentifierValue',
    },
    {
      type: 'input',
      id: 'incomingChargeAmount',
      maxValue: 126,
      allowNumericWithDecimal: true,
    },
    {
      id: 'chargeAmount',
      type: 'input',
      allowNumericWithDecimal: true,
      maxValue: 126,
    },
    {
      type: 'input',
      id: 'destination',
      noInitialWhiteSpace: true,
    },
    {
      type: 'input',
      id: 'weight',
    },
    {
      id: 'chargeType',
      type: 'select',
    },
    {
      type: 'select',
      id: 'agentChargeCode',
    },
    {
      type: 'input',
      id: 'agentChargeDescription',
      noInitialWhiteSpace: true,
    },
    {
      type: 'input',
      id: 'shipmentId',
    },
    {
      id: 'shipmentIdentifierId',
      type: 'extra',
    },
    {
      id: 'chargeTypeId',
      type: 'extra',
    },
    {
      type: 'extra',
      id: 'agentChargeCodeId',
    },
    {
      type: 'extra',
      id: 'isRowDisable',
    },
    {
      id: 'tempId',
      type: 'extra',
    },
    {
      id: 'invoiceChargesId',
      type: 'extra',
    },
    {
      id: 'destinationId',
      type: 'extra',
    },
    {
      id: 'action',
      type: 'extra',
    },
    {
      id: 'isLinehaulMismatch',
      type: 'extra',
    },
    {
      id: 'isUFOShipment',
      type: 'extra',
    },
  ],
};

export const defaultAPChargeGridData: IApChargeGridData[] = [
  {
    shipmentIdentifier: '',
    shipmentIdentifierValue: '',
    incomingChargeAmount: '',
    chargeAmount: '',
    destination: '',
    destinationId: '',
    weight: '',
    chargeType: '',
    chargeTypeId: '',
    agentChargeCode: '',
    agentChargeCodeId: '',
    agentChargeDescription: '',
    shipmentId: '',
    apChargeId: 0,
    action: 'create',
    tempId: '',
    isRowDisable: false,
    isInputRow: true,
    isLinehaulMismatch: null,
    isUFOShipment: false,
  },
];

export const APChargeColumnList = [
  'shipmentIdentifier',
  'shipmentIdentifierValue',
  'incomingChargeAmount',
  'chargeAmount',
  'destination',
  'weight',
  'chargeType',
  'agentChargeCode',
  'agentChargeDescription',
  'shipmentId',
];
export const AccountsPayableSectionHeadersName: string[] = [
  'AP Invoice Information',
  'AP Charges',
];

export const AccountsPayableSectionHeadersNameKey: string[] = [
  'apInvoiceInfo',
  'apCharges',
];

export const apInvoiceInfoHeaders: TableColumnsProp[] = [
  {
    key: 'agentName',
    label: 'Agent',
    isSelected: true,
    defaultPrintWidth: 150,
  },
  {
    key: 'invoiceNumber',
    label: 'Invoice Number',
    isSelected: true,
    defaultPrintWidth: 150,
  },
  {
    key: 'invoiceReference',
    label: 'Invoice Reference',
    isSelected: true,
    defaultPrintWidth: 150,
  },
  {
    key: 'invoiceDate',
    label: 'Invoice Date',
    isSelected: true,
    defaultPrintWidth: 120,
  },
  {
    key: 'incomingChargeCurrency',
    label: 'Incoming Charge Currency',
    isSelected: true,
    defaultPrintWidth: 180,
  },
  {
    key: 'conversionRate',
    label: 'Conversion Rate',
    isSelected: true,
    defaultPrintWidth: 140,
  },
  {
    key: 'notes',
    label: 'Notes',
    isSelected: true,
    defaultPrintWidth: 200,
  },
];

export const apChargesHeadersConfig: TableColumnsProp[] = [
  {
    key: 'shipmentIdentifier',
    defaultPrintWidth: 150,
    label: 'Shipment Identifier',
    isSelected: true,
  },
  {
    isSelected: true,
    key: 'shipmentIdentifierValue',
    label: 'Shipment Identifier Value',
    defaultPrintWidth: 150,
  },
  {
    label: 'Incoming Charge Amount',
    key: 'incomingChargeAmount',
    defaultPrintWidth: 150,
    isSelected: true,
  },
  {
    key: 'chargeAmount',
    defaultPrintWidth: 130,
    label: 'Charge Amount ($)',
    isSelected: true,
  },
  {
    key: 'destination',
    label: 'Destination',
    isSelected: true,
    defaultPrintWidth: 120,
  },
  { key: 'weight', label: 'Weight', isSelected: true, defaultPrintWidth: 80 },
  {
    key: 'agentChargeCode',
    label: 'Agent Charge Code',
    isSelected: true,
    defaultPrintWidth: 150,
  },
  {
    key: 'agentChargeDescription',
    label: 'Agent Charge Description',
    isSelected: true,
    defaultPrintWidth: 200,
  },
  {
    key: 'shipmentId',
    label: 'Shipment ID / MAWB ID',
    isSelected: true,
    defaultPrintWidth: 150,
  },
];

export const apPopuptablecolumns: TableColumnsProp[] = [
  {
    key: 'shipment',
    label: 'Shipment',
    ...isSelectedAndDefaultView,
  },
  {
    key: 'agentTrackingNumber',
    label: 'Agent Tracking Number',
    ...isSelectedAndDefaultView,
  },
  {
    key: 'weight',
    label: 'Weight',
    ...isSelectedAndDefaultView,
  },
  {
    key: 'destination',
    label: 'Destination',
    ...isSelectedAndDefaultView,
  },
  {
    key: 'invoiceNumber',
    label: 'Invoice Number',
    ...isSelectedAndDefaultView,
  },
];

export const apSftpFilesGridColumnConfig: TableColumnsProp[] = [
  {
    label: 'Agent Code / Agent Name',
    ...isSelectedAndDefaultView,
    key: 'agentData',
  },
  {
    label: 'File Name',
    key: 'fileName',
    ...isSelectedAndDefaultView,
  },
  {
    ...isSelectedAndDefaultView,
    key: 'fileDate',
    label: 'File Received Date',
  },
  {
    key: 'invoiceNumber',
    label: 'Invoice Number',
    ...isSelectedAndDefaultView,
  },
];
export const ApInfoTopSectionGridHeader = [
  'AgentCode',
  'InvoiceNumber',
  'InvoiceDate',
  'InvoiceReference',
  'IncomingChargeCurrency',
  'ConversionRate',
];

export const apDuplicateKeyChecks = [
  'agentChargeCode',
  'agentChargeDescription',
  'chargeAmount',
  'chargeType',
  'destination',
  'incomingChargeAmount',
  'shipmentId',
  'shipmentIdentifier',
  'shipmentIdentifierValue',
  'weight',
];

export const apSummaryDataTableConfig: DataTableConfig<AccountsPayableSummaryRow> =
  {
    rowIdField: 'id',
    entityName: BulkDeleteItemNames.accountsPayable,
    storageKey: GridColumnsStorageNames.accountsPayable,
    features: {
      selection: true,
      pagination: true,
      sorting: true,
      columnReorder: true,
      selectAllAcrossPages: true,
      columnCustomization: true,
    },
    messages: {
      noSearch: gridMessages.noAPSummarySearch,
      noMatch: gridMessages.noApCodeMatch,
      allPageSelected: MessageTemplates.AllItemsSelected,
      selectAll: MessageTemplates.SelectAllItems,
    },
    defaultPagination: {
      pageSize: 10,
      sortField: 'id',
      sortDirection: 'desc',
    },
    columns: [
      {
        key: 'invoiceNumber',
        label: 'Invoice Number',
        isHyperlink: true,
        sortable: true,
        isSelected: true,
        defaultView: true,
        defaultPrintWidth: 160,
        showTooltip: true,
      },
      {
        key: 'invoiceDate',
        label: 'Invoice Date',
        sortable: true,
        isSelected: true,
        defaultView: true,
        defaultPrintWidth: 140,
        showTooltip: true,
      },
      {
        key: 'agentName',
        label: 'Agent Name',
        sortable: true,
        isSelected: true,
        defaultView: true,
        defaultPrintWidth: 180,
        showTooltip: true,
      },
      {
        key: 'agentCode',
        label: 'Agent Code',
        sortable: true,
        isSelected: true,
        defaultView: true,
        defaultPrintWidth: 120,
        showTooltip: true,
      },
      {
        key: 'invoiceReference',
        label: 'Invoice Reference',
        sortable: true,
        isSelected: true,
        defaultView: true,
        defaultPrintWidth: 180,
        showTooltip: true,
      },
      {
        key: 'incomingChargeCurrency',
        label: 'Incoming Charge Currency',
        sortable: true,
        isSelected: false,
        defaultView: false,
        defaultPrintWidth: 180,
        showTooltip: true,
      },
      {
        key: 'conversionRate',
        label: 'Conversion Rate',
        sortable: true,
        isSelected: false,
        defaultView: false,
        defaultPrintWidth: 140,
      },
      {
        key: 'isPublish',
        label: 'Published to GP',
        sortable: true,
        isSelected: true,
        defaultView: true,
        defaultPrintWidth: 140,
      },
      {
        key: 'notes',
        label: 'Notes',
        sortable: true,
        isSelected: false,
        defaultView: false,
        defaultPrintWidth: 200,
        showTooltip: true,
      },
      {
        key: 'invoiceCharges',
        label: 'Invoice Charges ($)',
        sortable: true,
        isSelected: false,
        defaultView: false,
        defaultPrintWidth: 120,
      }
    ],
  };
